import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createMockTestPayload } from "../utils/apiPayload";
import { GetMockTestProcessingStatus } from "../../../Client/request";
import { navigateToScoreOrHome } from "../utils/navigation";
import { MakeQuestionAttempted } from "../../../Client/request";
import { getScore, getScoreMCMA, getHighlightIncorrectWordsScore } from "../utils/listeningScoring";
import moment from "moment";

export const useListeningSubmission = ({
  quest,
  questData,
  value,
  selectedOptions,
  selectedMultipleOptions,
  selectedAnswers,
  questionsTime,
  commulative,
  MockTestAttemptID,
  ExamId,
  step,
  totalQuestions,
  setStep,
  setCommulative,
  incrementSSTAttemptedCount,
  resetQuestionTimer,
  endTestAndTimeout,
  sstCompleted,
  setSSTCompleted,
  listeningNonSSTTime,
  resetStopwatch,
  sstAttemptedCount,
  sstCount,
  stopStopwatch,
  stopTimer,
  submitTriggerRef,
  navigateOnErrorPath = "/Mocktest",
}) => {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState();
  const [disabledButton, setDisabledButton] = useState(false);
  const [endModal, setEndModal] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  useEffect(() => {
    if (submitTriggerRef && submitTriggerRef.current && questData) {
      handleNext();
      submitTriggerRef.current = false;
    }
  }, [submitTriggerRef.current, questData]);

  const checkValidUserResponse = () => {
    if (!quest || !questData) {
      return false;
    }

    if (quest?.SubCategory === "Summarize Spoken Text" || quest?.SubCategory === "Write from Dictation") {
      return value?.trim() !== "";
    } else if (quest?.SubCategory === "Listening: Multiple Choice, Single Answer" || 
               quest?.SubCategory === "Highlight Correct Summary" || 
               quest?.SubCategory === "Select Missing Word") {
      return selectedOptions && selectedOptions !== "" && selectedOptions !== null && selectedOptions !== undefined;
    } else if (quest?.SubCategory === "Listening: Multiple Choice, Multiple Answers") {
      return selectedMultipleOptions?.length > 0;
    } else if (quest?.SubCategory === "Fill in the Blanks") {
      return questData && 
             questData?.AnswerNames?.length > 0 &&
             selectedOptions?.length === questData?.AnswerNames?.length && 
             selectedOptions?.every(answer => answer !== null && answer !== undefined && answer !== "");
    } else if (quest?.SubCategory === "Highlight Incorrect Words") {
      return selectedAnswers?.length > 0;
    }

    // Default case - if question type is not recognized, require some input
    return false;
  };

  const submitQuestionInBackground = async (payload) => {
    try {
      // Submit question in background without blocking UI
      const res = await MakeQuestionAttempted(payload);
      
      if (res.error) {
        console.error("Background submission error:", res.error);
        setNetworkError(true);
        setTimeout(() => {
          navigate(navigateOnErrorPath);
        }, 2000);
      } else {
        console.log("Question submitted successfully in background");
        if (!quest.IsAttempted) {
          quest.IsAttempted = true;
        }
      }
    } catch (error) {
      console.error("Background submission error:", error);
      setNetworkError(true);
    }
  };

  const handleNext = () => {
    console.log(`Submitting question ${step} and moving to next...`);
    
    if (!questData) {
      console.warn("questData is undefined, skipping submission");
      return;
    }
    
    // Check if user has valid response before proceeding
    const hasValidResponse = checkValidUserResponse();
    if (!hasValidResponse) {
      console.warn("No valid user response, skipping submission");
      // Still move to next question but don't submit
      resetQuestionTimer();
      if (step !== totalQuestions) {
        if (quest.SubCategory === "Summarize Spoken Text") {
          if (sstCount === 0 || sstCompleted) {
            setStep(step + 1);
            return;
          }

          if (sstAttemptedCount + 1 === sstCount) {
            setSSTCompleted(true);
            console.log(`SST done. Resetting stopwatch to Non SST Time ${listeningNonSSTTime} minutes.`);
            resetStopwatch(moment.duration(listeningNonSSTTime, "minutes").asMilliseconds());
            setCommulative(0);
            stopTimer();
          }
          incrementSSTAttemptedCount();
          setStep(step + 1);
        } else {
          setStep(step + 1);
        }
      } else if (step === totalQuestions) {
        // Handle final question - if no valid response, just end test
        if (typeof endTestAndTimeout === 'function') {
          endTestAndTimeout().then(() => {});
        }
      }
      return;
    }
    
    // Clean up any playing audio
    const audioElements = document.querySelectorAll('audio');
    audioElements.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    
    stopStopwatch();
    setCommulative(commulative + questionsTime / 1000);

    // Prepare payload based on question type
    let payload;
    if (quest.SubCategory === "Summarize Spoken Text") {
      payload = createMockTestPayload({
        quest,
        questData,
        userResponse: value,
        marksObtained: 100,
        timeTaken: questionsTime / 1000,
        commulative: null,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        aiResponse: {},
        audioUrl: null,
        selectedAnswers: [],
        correctAnswers: [],
        userOrder: [],
        majorAspects: [],
        minorAspects: [],
        optionNames: []
      });
    } else if (quest.SubCategory === "Fill in the Blanks") {
      let score = getScore(questData.AnswerNames, selectedOptions);
      payload = createMockTestPayload({
        quest,
        questData,
        userResponse: "",
        marksObtained: score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        aiResponse: {},
        audioUrl: null,
        selectedAnswers: selectedOptions,
        correctAnswers: questData.AnswerNames,
        userOrder: [],
        majorAspects: [],
        minorAspects: [],
        optionNames: []
      });
    } else if (quest.SubCategory === "Highlight Correct Summary" || quest.SubCategory === "Select Missing Word") {
      let score = getScore(questData.AnswerNames, [selectedOptions]);
      payload = createMockTestPayload({
        quest,
        questData,
        userResponse: "",
        marksObtained: score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        aiResponse: {},
        audioUrl: null,
        selectedAnswers: [selectedOptions],
        correctAnswers: questData.AnswerNames,
        userOrder: [],
        majorAspects: [],
        minorAspects: [],
        optionNames: []
      });
    } else if (quest.SubCategory === "Highlight Incorrect Words") {
      let score = getHighlightIncorrectWordsScore(selectedAnswers, questData.OptionNames);
      payload = createMockTestPayload({
        quest,
        questData,
        userResponse: "",
        marksObtained: score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        aiResponse: {},
        audioUrl: null,
        selectedAnswers: selectedAnswers,
        correctAnswers: questData.OptionNames,
        userOrder: [],
        majorAspects: [],
        minorAspects: [],
        optionNames: []
      });
    } else if (quest.SubCategory === "Listening: Multiple Choice, Multiple Answers") {
      let score = getScoreMCMA(questData.AnswerNames, selectedMultipleOptions);
      payload = createMockTestPayload({
        quest,
        questData,
        userResponse: "",
        marksObtained: score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        aiResponse: {},
        audioUrl: null,
        selectedAnswers: selectedMultipleOptions,
        correctAnswers: questData.AnswerNames,
        userOrder: [],
        majorAspects: [],
        minorAspects: [],
        optionNames: []
      });
    } else if (quest.SubCategory === "Listening: Multiple Choice, Single Answer") {
      let score = getScore(questData.AnswerNames, [selectedOptions]);
      score = score !== questData.AnswerNames.length ? 0 : score;
      payload = createMockTestPayload({
        quest,
        questData,
        userResponse: "",
        marksObtained: score,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        aiResponse: {},
        audioUrl: null,
        selectedAnswers: [selectedOptions],
        correctAnswers: questData.AnswerNames,
        userOrder: [],
        majorAspects: [],
        minorAspects: [],
        optionNames: []
      });
    } else if (quest.SubCategory === "Write from Dictation") {
      payload = createMockTestPayload({
        quest,
        questData,
        userResponse: value,
        marksObtained: 100,
        timeTaken: questionsTime / 1000,
        commulative,
        mockTestAttemptID: MockTestAttemptID,
        examId: ExamId,
        aiResponse: {},
        audioUrl: null,
        selectedAnswers: [],
        correctAnswers: [],
        userOrder: [],
        majorAspects: [],
        minorAspects: [],
        optionNames: []
      });
    }

    // Submit in background immediately (fire and forget)
    if (payload) {
      // Use setTimeout to ensure this runs after the current execution stack
      setTimeout(() => {
        submitQuestionInBackground(payload);
      }, 0);
    }

    // Move to next question immediately
    resetQuestionTimer();
    if (step !== totalQuestions) {
      if (quest.SubCategory === "Summarize Spoken Text") {
        if (sstCount === 0 || sstCompleted) {
          setStep(step + 1);
          return;
        }

        if (sstAttemptedCount + 1 === sstCount) {
          setSSTCompleted(true);
          console.log(`SST done. Resetting stopwatch to Non SST Time ${listeningNonSSTTime} minutes.`);
          resetStopwatch(moment.duration(listeningNonSSTTime, "minutes").asMilliseconds());
          setCommulative(0);
          stopTimer();
        }
        incrementSSTAttemptedCount();
        setStep(step + 1);
      } else {
        setStep(step + 1);
      }
    } else if (step === totalQuestions) {
      // Handle final question submission; defer ending to parent
      if (payload) {
        MakeQuestionAttempted(payload).then((res) => {
          if (res.error) {
            setNetworkError(true);
            setTimeout(() => {
              navigate(navigateOnErrorPath);
            }, 2000);
          } else {
            if (!quest.IsAttempted) {
              quest.IsAttempted = true;
            }
            if (typeof endTestAndTimeout === 'function') {
              endTestAndTimeout().then(() => {});
            }
          }
        });
      } else if (typeof endTestAndTimeout === 'function') {
        endTestAndTimeout().then(() => {});
      }
    }
  };

  return {
    isSubmitted,
    setIsSubmitted,
    disabledButton,
    setDisabledButton,
    endModal,
    setEndModal,
    networkError,
    setNetworkError,
    handleNext,
  };
};
